import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  FileText,
  ImageIcon,
  Sparkles,
  Upload,
  X,
  MapPin,
  Download,
  ExternalLink,
} from "lucide-react";
import { smartDesignUpload } from "../../api/upload.api.js";
import { assetUrl } from "../../api/client.js";
import { Button } from "../../components/ui/Button.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { cn } from "../../lib/cn.js";

/**
 * "Smart Upload" — admin drops a floor-plan PDF + a hero image, the
 * backend extracts specs, renders a branded floor-plan tile, generates
 * a professional brochure PDF, and returns everything we need to
 * pre-fill the design form.
 *
 * Flow:
 *   1. Drop PDF + image → click Run Smart Upload.
 *   2. Review the extracted specs.
 *   3. Confirm/edit the auto-generated Design name + Description here
 *      (these aren't reliably extractable from the PDF, so admin owns
 *      them).
 *   4. Click "Continue" → land on the Design form, every field
 *      pre-populated, ready to save.
 */

const ACCEPT_PDF = "application/pdf";
const ACCEPT_IMG = "image/jpeg,image/png,image/webp,image/avif";

function FileSlot({ icon: Icon, label, hint, accept, file, onPick, onClear }) {
  const inputRef = useRef(null);
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border-2 border-dashed p-6 transition-colors",
        file ? "border-accent/60 bg-accent/5" : "border-border-subtle bg-bg-elevated-2 hover:border-accent"
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "grid h-10 w-10 place-items-center rounded-md",
            file ? "bg-accent/15 text-accent" : "bg-bg-base text-text-muted"
          )}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="font-medium text-text-primary">{label}</p>
          <p className="truncate text-xs text-text-muted">{file ? file.name : hint}</p>
        </div>
        {file && (
          <button
            type="button"
            onClick={onClear}
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-bg-elevated hover:text-danger"
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
        }}
      />
      {!file && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-border-subtle bg-bg-base px-4 py-2.5 text-sm text-text-secondary hover:border-accent hover:text-accent"
        >
          <Upload className="h-4 w-4" /> Choose file
        </button>
      )}
    </div>
  );
}

/**
 * Compose a baseline marketing description from the extracted specs. The
 * admin sees this in an editable textarea and can rewrite it before
 * continuing. Same algorithm as the backend's buildAutoDescription so the
 * brochure and the form agree if admin doesn't edit.
 */
function buildAutoDescription(ex) {
  if (!ex) return "";
  const parts = [];
  if (ex.bedrooms && ex.bathrooms) {
    parts.push(
      `${ex.bedrooms}-bedroom, ${ex.bathrooms}-bathroom ${ex.storey ?? ""} home`.trim()
    );
  }
  if (ex.garage) {
    parts.push(
      `with a ${
        ex.garage === 2 ? "double" : ex.garage === 1 ? "single" : `${ex.garage}-car`
      } garage`
    );
  }
  if (ex.areaSqm) {
    parts.push(
      `offering ${ex.areaSqm} m²${ex.areaSq ? ` (${ex.areaSq} sq)` : ""} of internal space`
    );
  }
  if (parts.length === 0) return "";
  const sentence = parts.join(", ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

function defaultDesignName(file) {
  if (!file?.name) return "";
  // "ManorPlan-32-final.pdf" → "ManorPlan 32 final"
  return file.name
    .replace(/\.[^.]+$/, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function SmartUploadPage() {
  const navigate = useNavigate();
  const [pdf, setPdf] = useState(null);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // Confirmable fields — name + description sit on the SmartUpload screen
  // because they're never reliably extractable, but the admin still wants
  // a quick path. Pre-fill from the PDF filename + auto-description, let
  // them edit before we forward to the design form.
  const [designName, setDesignName] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");

  useEffect(() => {
    document.title = "Smart Upload · Admin";
  }, []);

  const canUpload = pdf && image && !uploading;

  const handleProcess = async () => {
    if (!canUpload) return;
    setUploading(true);
    try {
      const r = await smartDesignUpload(pdf, image);
      setResult(r);
      // Seed editable fields from the response.
      setDesignName(r?.extracted?.designNameGuess || defaultDesignName(pdf));
      setDescription(r?.extracted?.autoDescription || buildAutoDescription(r?.extracted));
      setAddress(r?.extracted?.address || r?.extracted?.addressCandidates?.[0] || "");
      toast.success("Smart upload processed");
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message ?? "Smart upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Build the prefill payload for the Design Form. We only set fields the
   * extractor was confident about — empty strings stay defaults.
   */
  const buildPrefill = () => {
    if (!result) return {};
    const ex = result.extracted ?? {};
    const files = result.files ?? {};
    return {
      // Editable on this screen — the admin owns these.
      ...(designName ? { name: designName } : {}),
      ...(description ? { description } : {}),
      ...(address ? { address } : {}),
      // Pictures + files
      heroImage: files.imageUrl ?? "",
      images: files.imageUrl ? [files.imageUrl] : [],
      floorPlanImages: files.floorPlanUrl ? [files.floorPlanUrl] : [],
      floorPlanPdf: files.pdfUrl ?? "",
      ...(files.brochureUrl ? { generatedBrochurePdf: files.brochureUrl } : {}),
      // Specs (only when present)
      ...(ex.storey ? { storey: ex.storey } : {}),
      ...(Number.isFinite(ex.bedrooms) ? { bedrooms: ex.bedrooms } : {}),
      ...(Number.isFinite(ex.bathrooms) ? { bathrooms: ex.bathrooms } : {}),
      ...(Number.isFinite(ex.garage) ? { garage: ex.garage } : {}),
      ...(Number.isFinite(ex.areaSqm) ? { areaSqm: ex.areaSqm } : {}),
      ...(Number.isFinite(ex.areaSq) ? { areaSq: ex.areaSq } : {}),
    };
  };

  const handleContinue = () => {
    if (!result) return;
    if (!designName.trim()) {
      toast.error("Design name is required before continuing");
      return;
    }
    navigate("/designs/new", { state: { prefill: buildPrefill() } });
  };

  const handleReset = () => {
    setResult(null);
    setPdf(null);
    setImage(null);
    setDesignName("");
    setDescription("");
    setAddress("");
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-accent">
          <Sparkles className="h-3.5 w-3.5" /> Smart Upload
        </p>
        <h1 className="mt-2 font-display text-2xl text-text-primary">Add a design from a PDF</h1>
        <p className="mt-1 max-w-2xl text-sm text-text-secondary">
          Drop the architectural drawings PDF and a hero photo. We&apos;ll read the floor plan,
          extract the specs (storey, bedrooms, baths, area, address), render a branded
          floor-plan tile, and generate a professional multi-page brochure. You confirm the
          name + description and save.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FileSlot
          icon={FileText}
          label="Floor plan PDF"
          hint="Architectural drawings — typically 12-20 pages, A3."
          accept={ACCEPT_PDF}
          file={pdf}
          onPick={setPdf}
          onClear={() => setPdf(null)}
        />
        <FileSlot
          icon={ImageIcon}
          label="Hero photo"
          hint="The exterior shot for the design listing card."
          accept={ACCEPT_IMG}
          file={image}
          onPick={setImage}
          onClear={() => setImage(null)}
        />
      </div>

      <div>
        <Button onClick={handleProcess} disabled={!canUpload} loading={uploading} size="lg">
          <Sparkles className="mr-2 h-4 w-4" />
          {uploading ? "Reading PDF…" : "Run Smart Upload"}
        </Button>
      </div>

      {result && (
        <ExtractionPreview
          result={result}
          designName={designName}
          setDesignName={setDesignName}
          description={description}
          setDescription={setDescription}
          address={address}
          setAddress={setAddress}
          onContinue={handleContinue}
          onReset={handleReset}
        />
      )}
    </div>
  );
}

function ExtractionPreview({
  result,
  designName,
  setDesignName,
  description,
  setDescription,
  address,
  setAddress,
  onContinue,
  onReset,
}) {
  const ex = result.extracted ?? {};
  const files = result.files ?? {};
  const meta = result.pdf ?? {};

  const detectedFields = [
    { label: "Storey", value: ex.storey ?? null },
    { label: "Bedrooms", value: ex.bedrooms ?? null },
    { label: "Bathrooms", value: ex.bathrooms ?? null },
    { label: "Garage", value: ex.garage ?? null },
    { label: "Area (m²)", value: ex.areaSqm ?? null },
    { label: "Area (sq)", value: ex.areaSq ?? null },
  ];

  const mapEmbedSrc = useMemo(() => {
    if (!address?.trim()) return null;
    return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  }, [address]);

  return (
    <div className="space-y-6 rounded-2xl border border-accent/20 bg-bg-elevated p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Review &amp; confirm</p>
          <h2 className="mt-1 font-display text-xl text-text-primary">
            {detectedFields.filter((f) => f.value != null).length} of {detectedFields.length} specs
            detected
          </h2>
          <p className="mt-1 text-xs text-text-muted">
            PDF had {meta.pages ?? "?"} pages
            {meta.groundFloorPage ? ` · ground floor on page ${meta.groundFloorPage}` : ""}.
            Edit the name + description below — those aren&apos;t extractable, so they&apos;re
            yours to author.
          </p>
        </div>
      </div>

      {/* Editable fields */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Input
          label="Design name"
          required
          value={designName}
          onChange={(e) => setDesignName(e.target.value)}
          placeholder="e.g. Manor 32"
          hint="Pre-filled from the PDF filename. Pick the name you'll market under."
        />
        <Input
          label="Property address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="e.g. 12 Sullivan Street, Lucas VIC 3350"
          hint={
            ex.addressCandidates?.length > 1
              ? `Other candidates: ${ex.addressCandidates.slice(0, 4).join(" · ")}`
              : "Used for the public detail-page map."
          }
        />
      </div>

      <Textarea
        label="Description"
        rows={4}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        hint="Auto-generated from the specs. Rewrite for marketing tone — visitors see this on the public page and inside the brochure."
      />

      {/* Visual + structural preview */}
      <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-3">
          {files.floorPlanUrl ? (
            <PreviewCard
              src={assetUrl(files.floorPlanUrl)}
              caption="Branded floor-plan tile · auto-rendered + composed"
            />
          ) : (
            <div className="rounded-lg border border-dashed border-border-subtle p-4 text-sm text-text-muted">
              Floor plan rendering wasn&apos;t available — drop a manual one on the form screen.
            </div>
          )}

          {files.imageUrl && (
            <PreviewCard src={assetUrl(files.imageUrl)} caption="Hero image · uploaded as-is" />
          )}

          {mapEmbedSrc && (
            <div className="overflow-hidden rounded-lg border border-border-subtle bg-bg-base">
              <iframe
                title="Site map preview"
                src={mapEmbedSrc}
                loading="lazy"
                className="block h-56 w-full border-0"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <p className="flex items-center gap-2 border-t border-border-subtle px-3 py-2 text-xs text-text-muted">
                <MapPin className="h-3.5 w-3.5 text-accent" /> Map preview · {address}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4 self-start">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-border-subtle bg-bg-elevated-2 p-4 text-sm">
            {detectedFields.map((f) => (
              <div key={f.label} className="contents">
                <dt className="text-xs uppercase tracking-wider text-text-muted">{f.label}</dt>
                <dd
                  className={cn(
                    f.value != null ? "text-text-primary" : "italic text-text-muted"
                  )}
                >
                  {f.value ?? "—"}
                </dd>
              </div>
            ))}
          </dl>

          {/* Brochure card */}
          <div className="rounded-lg border border-accent/30 bg-accent/5 p-4">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-accent/15 text-accent">
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary">Brochure</p>
                <p className="mt-0.5 text-xs text-text-muted">
                  {files.brochureUrl
                    ? "Auto-generated · cover, specs, plan, map, contact."
                    : "Will be generated on next regenerate — couldn't compose this run."}
                </p>
              </div>
            </div>
            {files.brochureUrl && (
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={assetUrl(files.brochureUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-border-subtle bg-bg-base px-3 py-1.5 text-xs text-text-secondary hover:border-accent hover:text-accent"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Preview
                </a>
                <a
                  href={assetUrl(files.brochureUrl)}
                  download
                  className="inline-flex items-center gap-2 rounded-md border border-border-subtle bg-bg-base px-3 py-1.5 text-xs text-text-secondary hover:border-accent hover:text-accent"
                >
                  <Download className="h-3.5 w-3.5" /> Download PDF
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-border-subtle pt-5">
        <Button onClick={onContinue}>
          Continue to design form <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button variant="secondary" onClick={onReset}>
          Try another PDF
        </Button>
        <p className="ml-auto text-xs text-text-muted">
          Every field still appears on the next screen — adjust anything before saving.
        </p>
      </div>
    </div>
  );
}

function PreviewCard({ src, caption }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border-subtle bg-bg-base">
      <img src={src} alt="" className="block w-full" />
      <p className="border-t border-border-subtle px-3 py-2 text-xs text-text-muted">{caption}</p>
    </div>
  );
}
